import { memo } from "react";
import { useTranslation } from "react-i18next";
import { ProjectSettlementsCommentsService } from "@/services/projectSettlementsComments";
import {
  projectSettlementsCommentsWrite,
  WriteProjectSettlementsComments,
} from "@/schemas/projectSettlementsComments";
import Form from "@/components/Form";
import { DefaultValues, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import SaveButton from "@/components/SaveButton";
import { Textarea } from "@/components/ui/textarea";
import { isRequired } from "@/lib/form";
import Field from "@/components/Field";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { ApiError } from "@/services/api";

interface AddCommentCreatorProps {
  settlementId: string;
  isPending: boolean;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
  errorTitle?: string;
}

const AddCommentCreatorComponent = ({
  settlementId,
  isPending: isPendingFromProps,
  onSuccess,
  onError,
}: AddCommentCreatorProps) => {
  const { t } = useTranslation();

  const { mutateAsync: createComment, isPending: createCommentPending } =
    useMutationWithErrorHandling({
      mutationFn: (data: WriteProjectSettlementsComments) =>
        ProjectSettlementsCommentsService.post(settlementId, data),
      onSuccess: () => {
        onSuccess?.();
        form.reset();
      },
      onError,
      errorTitle: t("projectSettlementsComments.error"),
    });

  const isPending = createCommentPending || isPendingFromProps;

  const form = useForm<WriteProjectSettlementsComments>({
    resolver: zodResolver(projectSettlementsCommentsWrite),
    defaultValues: {
      comment: "",
      settlement_id: settlementId,
    } as DefaultValues<WriteProjectSettlementsComments>,
    disabled: isPending,
  });

  useFormUnsavedChangesWarning(form);

  return (
    <Form className="mt-2" form={form} handleSubmit={createComment}>
      <Field
        name="comment"
        className="w-full"
        required={isRequired(projectSettlementsCommentsWrite.shape.comment)}
        render={({ field }) => (
          <Textarea
            className="w-full"
            placeholder={t("comments.addPlaceholder")}
            {...field}
          />
        )}
      />
      <div className="flex justify-end mt-2">
        <SaveButton className="w-auto" />
      </div>
    </Form>
  );
};

export const AddCommentCreator = memo(AddCommentCreatorComponent);
